/**
 * Offline drainer for nutrition offline queues:
 *   - db.nutritionComboStarts / db.nutritionComboCompletes (foliar combos, Slice 3)
 *
 * Foliar combo items are POSTed to:
 *   /nutrition/operator-sessions/:comboId/start
 *   /nutrition/operator-sessions/:comboId/complete
 *
 * Status machine mirrors nutritionSignoffSyncService (and fieldScoutSyncService):
 *   pending → syncing → synced | failed | quarantined (at 5 retries).
 *
 * F6: the legacy soil queues (db.nutritionSoilStarts / db.nutritionSoilCompletes,
 * added in Nutrition Slice 4) are no longer drained here — their routes are gone.
 * The Dexie TABLES are deliberately KEPT; see the docstring on
 * resetNutritionComboSyncState below for why.
 */
import axios from 'axios';
import { db } from '../db';

const QUARANTINE_THRESHOLD = 5;
let inProgress = false;

export function __resetNutritionComboSyncFlagForTesting(): void { inProgress = false; }

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const baseUrl = process.env.REACT_APP_API_URL || '/api/v1';

/**
 * Reset items stuck in 'syncing' (crash/refresh) and 'quarantined' back to pending.
 *
 * F6: only the foliar combo queues below are reset — the legacy soil queues
 * (db.nutritionSoilStarts / db.nutritionSoilCompletes) are NOT, because
 * syncPendingNutritionCombos() no longer drains them (their routes are gone).
 * Resetting a queue nothing drains would be a no-op at best. The Dexie TABLES
 * themselves are still deliberately kept: dropping one would discard its rows,
 * and a queued item lives on an operator's device where no server-side census
 * can see it. db.signoffQueueRemoved.test.ts anchors on nutritionSoilStarts to
 * prove the table survives.
 */
export const resetNutritionComboSyncState = async (): Promise<number> => {
  let count = 0;
  // ── Foliar combo queues ───────────────────────────────────────────────────
  count += await db.nutritionComboStarts
    .where('syncStatus').equals('syncing')
    .modify({ syncStatus: 'pending' });
  count += await db.nutritionComboStarts
    .where('syncStatus').equals('quarantined')
    .modify({ syncStatus: 'pending', retryCount: 0 });
  count += await db.nutritionComboCompletes
    .where('syncStatus').equals('syncing')
    .modify({ syncStatus: 'pending' });
  count += await db.nutritionComboCompletes
    .where('syncStatus').equals('quarantined')
    .modify({ syncStatus: 'pending', retryCount: 0 });
  return count;
};

export const syncPendingNutritionCombos = async (): Promise<void> => {
  if (inProgress) return;
  if (!navigator.onLine) return;
  inProgress = true;
  try {
    // ── Drain start queue ────────────────────────────────────────────────────
    const pendingStarts = await db.nutritionComboStarts
      .where('syncStatus').anyOf(['pending', 'failed'])
      .toArray();
    if (pendingStarts.length > 0) {
      console.log(`[NutritionComboSync] Draining ${pendingStarts.length} queued start(s)...`);
    }
    for (const item of pendingStarts) {
      try {
        await db.nutritionComboStarts.update(item.localId!, { syncStatus: 'syncing' });
        await axios.post(
          `${baseUrl}/nutrition/operator-sessions/${item.comboId}/start`,
          item.payload,
          { headers: authHeaders() },
        );
        await db.nutritionComboStarts.update(item.localId!, { syncStatus: 'synced' });
        console.log(`[NutritionComboSync] Start item ${item.localId} (combo: ${item.comboId}) synced.`);
      } catch (err) {
        const newRetryCount = (item.retryCount ?? 0) + 1;
        const newStatus = newRetryCount >= QUARANTINE_THRESHOLD ? 'quarantined' : 'failed';
        console.warn(`[NutritionComboSync] Start item ${item.localId} ${newStatus} (attempt ${newRetryCount}):`, err);
        await db.nutritionComboStarts.update(item.localId!, { syncStatus: newStatus, retryCount: newRetryCount });
      }
    }

    // ── Drain complete queue ─────────────────────────────────────────────────
    const pendingCompletes = await db.nutritionComboCompletes
      .where('syncStatus').anyOf(['pending', 'failed'])
      .toArray();
    if (pendingCompletes.length > 0) {
      console.log(`[NutritionComboSync] Draining ${pendingCompletes.length} queued complete(s)...`);
    }
    for (const item of pendingCompletes) {
      try {
        await db.nutritionComboCompletes.update(item.localId!, { syncStatus: 'syncing' });
        await axios.post(
          `${baseUrl}/nutrition/operator-sessions/${item.comboId}/complete`,
          item.payload,
          { headers: authHeaders() },
        );
        await db.nutritionComboCompletes.update(item.localId!, { syncStatus: 'synced' });
        console.log(`[NutritionComboSync] Complete item ${item.localId} (combo: ${item.comboId}) synced.`);
      } catch (err) {
        const newRetryCount = (item.retryCount ?? 0) + 1;
        const newStatus = newRetryCount >= QUARANTINE_THRESHOLD ? 'quarantined' : 'failed';
        console.warn(`[NutritionComboSync] Complete item ${item.localId} ${newStatus} (attempt ${newRetryCount}):`, err);
        await db.nutritionComboCompletes.update(item.localId!, { syncStatus: newStatus, retryCount: newRetryCount });
      }
    }
  } finally {
    inProgress = false;
  }
};

/**
 * How many queued foliar ACTIONS are still owed, across BOTH queues.
 *
 * Counts ACTIONS, not combos (U5's ruling): two queued actions on one combo are
 * two things the operator is waiting on.
 *
 * All four non-terminal statuses count as owed; only 'synced' is done:
 *   - 'pending' / 'failed' — waiting to sync, or waiting for a retry.
 *   - 'quarantined' — the drain gave up at QUARANTINE_THRESHOLD with no
 *     user-facing surface anywhere; excluding it would make the worklist
 *     banner drop to zero and tell the operator their work had synced when
 *     it had not.
 *   - 'syncing' — a crash or refresh mid-POST leaves an item durably in this
 *     state; excluding it reproduces the exact same false-zero the
 *     'quarantined' rule above exists to prevent.
 * resetNutritionComboSyncState() recovers both 'quarantined' and 'syncing'
 * items back to 'pending' on the next worklist mount, so an item in either
 * state is genuinely still in flight rather than abandoned.
 */
const OWED: Array<'pending' | 'syncing' | 'failed' | 'quarantined'> =
  ['pending', 'syncing', 'failed', 'quarantined'];

export async function readPendingFoliarComboCount(): Promise<number> {
  const [starts, completes] = await Promise.all([
    db.nutritionComboStarts.where('syncStatus').anyOf(OWED).count(),
    db.nutritionComboCompletes.where('syncStatus').anyOf(OWED).count(),
  ]);
  return starts + completes;
}

/** The comboIds with work still owed — one entry each, drives the queued row marker. */
export async function readPendingFoliarComboKeys(): Promise<string[]> {
  const [starts, completes] = await Promise.all([
    db.nutritionComboStarts.where('syncStatus').anyOf(OWED).toArray(),
    db.nutritionComboCompletes.where('syncStatus').anyOf(OWED).toArray(),
  ]);
  const items: Array<{ comboId: string }> =
    (starts as Array<{ comboId: string }>).concat(completes as Array<{ comboId: string }>);
  // es5 target: Array.from, never [...new Set(x)] — the spread passes jest and
  // breaks `npm run build`.
  return Array.from(new Set(items.map((i) => i.comboId)));
}
